/**
 * Зеркало серверной модели прав (server/src/auth/permissions.ts) для фронта.
 * Источник истины — БД; здесь типы + хелперы гейтинга UI.
 */

export type CityScope = 'all' | 'own';
export type GamesScope = 'all' | 'own';

export interface PagePermissions {
  dashboard: boolean;
  calendar: boolean;
  sales: boolean;
  reports: boolean;
  personnel: boolean;
  /**
   * Только вкладка «Загрузка» раздела «Персонал» (отработанные игры/часы за месяц) — без
   * списка сотрудников и бригад. Заведено под кассира. У кого есть personnel — вкладка
   * доступна и без этого права.
   */
  workload: boolean;
  /** Справочник ставок на расчёт зарплаты — защищённый экран (финансист/руководитель/супер-админ). */
  pay_rates: boolean;
}

export interface DealPermissions {
  create: boolean;
  delete: boolean;
  edit: boolean;
  assignTeam: boolean;
  comment: boolean;
  attach: boolean;
  viewHistory: boolean;
  financeOnly: boolean;
  /** Право заполнять анкету ОКК (опрос качества) — даже в закрытой карточке. */
  okk: boolean;
  /** Право редактировать сами вопросы анкеты ОКК (конструктор форм). */
  okk_edit: boolean;
  /** Право ставить ЗЕЛЁНЫЕ галочки (декларация «сделал») на финансовых пунктах. */
  declareGreen: boolean;
  /** Право подтверждать (зелёная → ФИОЛЕТОВАЯ) — аудит финансиста. */
  confirmPurple: boolean;
  /**
   * Узкое право «кассир»: вписать ТОЛЬКО фактическую сумму (actual_amount) и приложить
   * чеки. Остальной финблок и ядро карточки такому пользователю недоступны (вариант B).
   */
  editActualAmount: boolean;
}

export interface RolePermissions {
  pages: PagePermissions;
  deals: DealPermissions;
  scope: { cities: CityScope; games: GamesScope };
  citySwitcher: boolean;
}

/** Текущий пользователь — ровно то, что отдаёт /api/auth/me. */
export interface CurrentUser {
  id: string;
  kind: 'bitrix' | 'local';
  login?: string | null;
  full_name: string;
  email: string | null;
  access_role: string | null;
  access_role_name: string | null;
  worker_type: string | null;
  worker_type_name: string | null;
  employee_id: string | null;
  city_ids: string[];
  is_active: boolean;
  avatar?: string | null;
  telegram_linked?: boolean;
  permissions: RolePermissions;
}

export const EMPTY_PERMISSIONS: RolePermissions = {
  pages: { dashboard: false, calendar: false, sales: false, reports: false, personnel: false, workload: false, pay_rates: false },
  deals: {
    create: false, delete: false, edit: false, assignTeam: false,
    comment: false, attach: false, viewHistory: false, financeOnly: false, okk: false, okk_edit: false,
    declareGreen: false, confirmPurple: false, editActualAmount: false,
  },
  scope: { cities: 'own', games: 'own' },
  citySwitcher: false,
};

export const FULL_PERMISSIONS: RolePermissions = {
  pages: { dashboard: true, calendar: true, sales: true, reports: true, personnel: true, workload: true, pay_rates: true },
  deals: {
    create: true, delete: true, edit: true, assignTeam: true,
    comment: true, attach: true, viewHistory: true, financeOnly: false, okk: true, okk_edit: true,
    declareGreen: true, confirmPurple: true, editActualAmount: true,
  },
  scope: { cities: 'all', games: 'all' },
  citySwitcher: true,
};

/** Права пользователя (или пустые, если не авторизован). */
export function perms(user: CurrentUser | null | undefined): RolePermissions {
  return user?.permissions ?? EMPTY_PERMISSIONS;
}

/**
 * Раздел «Филиалы» — только руководитель сети и супер-админ. Зеркало серверной проверки
 * canManageBranches (server/src/auth/guards.ts): право «Персонал» тут не годится, под него
 * попадает управляющий филиала, а заводить и сносить филиалы сети он не должен.
 */
export function canManageBranches(user: CurrentUser | null | undefined): boolean {
  return user?.access_role === 'owner' || user?.access_role === 'superadmin';
}

/** Синтетический пользователь-владелец для локального dev-режима (без backend). */
export const LOCAL_DEV_USER: CurrentUser = {
  id: 'local-dev',
  kind: 'local',
  full_name: 'Локальный режим',
  email: null,
  access_role: 'owner',
  access_role_name: 'Руководитель',
  worker_type: null,
  worker_type_name: null,
  employee_id: null,
  city_ids: [],
  is_active: true,
  permissions: FULL_PERMISSIONS,
};
